import { useEffect } from 'react';
import gsap from 'gsap';
import { useRouter } from 'next/router';
import { debounce } from 'lodash';

export const useReadMoreAnimation = () => {
  const { route } = useRouter();

  useEffect(() => {
    const mediaQuery = window.matchMedia('(min-width: 768px)');

    const resetStyles = () => {
      const readMoreHoverContents = document.querySelectorAll('.read-more-hover-content');
      readMoreHoverContents.forEach(element => {
        const titleDescriptionWrapper = element.querySelector(
          '.title-description-wrapper',
        ) as HTMLElement;
        const description = titleDescriptionWrapper.querySelector('p') as HTMLElement;
        const button = element.querySelector('.btn') as HTMLElement;

        // Reset initial styles
        gsap.set(description, { opacity: 0 });
        gsap.set(titleDescriptionWrapper, { y: `+${description.offsetHeight}px` });
        gsap.set(button, { opacity: 1, y: 0 });
      });
    };

    const undoStyles = () => {
      const readMoreHoverContents = document.querySelectorAll('.read-more-hover-content');
      readMoreHoverContents.forEach(element => {
        const titleDescriptionWrapper = element.querySelector(
          '.title-description-wrapper',
        ) as HTMLElement;
        const description = titleDescriptionWrapper.querySelector('p') as HTMLElement;
        const button = element.querySelector('.btn') as HTMLElement;

        // Undo styles
        gsap.set(description, { opacity: '' });
        gsap.set(titleDescriptionWrapper, { y: '' });
        gsap.set(button, { opacity: '', y: '' });
      });
    };

    const handleMediaChange = (e: MediaQueryListEvent) => {
      const readMoreHoverContents = document.querySelectorAll('.read-more-hover-content');
      if (e.matches) {
        resetStyles();

        readMoreHoverContents.forEach(element => {
          const titleDescriptionWrapper = element.querySelector(
            '.title-description-wrapper',
          ) as HTMLElement;
          const title = titleDescriptionWrapper.querySelector('h3') as HTMLElement;
          const description = titleDescriptionWrapper.querySelector('p') as HTMLElement;
          const button = element.querySelector('.btn') as HTMLElement;

          element.addEventListener('mouseenter', () => {
            const descriptionHeight = description.offsetHeight;
            if (window.innerWidth > 768) {
              gsap.to(button, { opacity: 0, duration: 0 });
              gsap.to(button, { y: `+${descriptionHeight}px`, duration: 0 });
              gsap.to(titleDescriptionWrapper, { y: 0, duration: 0.6 });
              gsap.to(description, { opacity: 1, duration: 0.6 });
              gsap.to(button, { opacity: 1, duration: 0.6 });
              gsap.to(button, { y: 0, delay: 0, duration: 0.6 });
            }
          });

          element.addEventListener('mouseleave', () => {
            if (window.innerWidth > 768) {
              gsap.to(button, { opacity: 0, duration: 0 });
              gsap.to(button, { y: `-${description.offsetHeight}px`, duration: 0 });
              gsap.to(titleDescriptionWrapper, {
                y: `+${description.offsetHeight}px`,
                duration: 0.6,
              });
              gsap.to(description, { opacity: 0, duration: 0.6 });
              gsap.to(button, { opacity: 1, duration: 0.6 });
              gsap.to(button, { y: 0, duration: 0.6 });
            }
          });
        });
      }
    };

    // Initial check and reset styles
    if (mediaQuery.matches) {
      resetStyles();
      handleMediaChange({ matches: true } as MediaQueryListEvent);
    } else {
      undoStyles();
    }

    // Add listener for media query changes
    mediaQuery.addEventListener('change', handleMediaChange);

    // Add listener for window resize
    window.addEventListener('resize', () => {
      if (window.innerWidth >= 768) {
        debounce(resetStyles, 100)();
      } else {
        undoStyles();
      }
    });

    // Cleanup listeners on unmount
    return () => {
      mediaQuery.removeEventListener('change', handleMediaChange);
      window.removeEventListener('resize', () => {
        if (window.innerWidth >= 768) {
          resetStyles();
        } else {
          undoStyles();
        }
      });
    };
  }, [route]);
};

export default useReadMoreAnimation;
